import { Form, Formik, FormikProps } from 'formik';
import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { GradeDto } from 'src/store/grades/grade';
import { GradeApi } from 'src/store/grades/grades.api';
import { ProductNRunDto } from 'src/store/products/product';
import { RunSystemDto } from 'src/store/runsystems/runsystem';
import { selectActiveRunSystems } from 'src/store/runsystems/runsystems.selectors';
import { SpecificationDto } from 'src/store/specifications/specification';
import { SpecificationApi } from 'src/store/specifications/specifications.api';
import {
  maxValue,
  minValue,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  onCancel: () => void;
  onSubmit: (productNRun: ProductNRunDto) => void;
};

export const ProductsNRunForm = forwardRef<FormikProps<ProductNRunDto>, Props>(
  (props, ref) => {
    const { onSubmit, onCancel } = props;
    const { t } = useTranslation();
    const [showErrors, setShowErrors] = useState<boolean>(false);

    const [productNRun] = useState<ProductNRunDto>({} as ProductNRunDto);
    const [grades, setGrades] = useState<GradeDto[]>([]);
    const [specifications, setSpecifications] = useState<SpecificationDto[]>(
      []
    );

    const runSystems = useSelector(selectActiveRunSystems);

    const onRunSystemChange = async (runSystem: Nullable<RunSystemDto>) => {
      if (runSystem) {
        const response = await GradeApi.getByRunSystem(runSystem.id);
        if (response.ok) {
          setGrades(response.data);
        }
      } else {
        setGrades([]);
        setSpecifications([]);
      }
    };

    const onGradeChange = async (grade: Nullable<GradeDto>) => {
      if (grade) {
        const response = await SpecificationApi.getByGrade(grade.id);
        if (response.ok) {
          setSpecifications(response.data);
        }
      } else {
        setSpecifications([]);
      }
    };

    const validate = (nRun: ProductNRunDto) => {
      const validationRunners = [
        validationRunner(
          'count',
          t('props.count'),
          required,
          minValue(2),
          maxValue(20)
        ),
        validationRunner('runSystemId', t('runSystem.singular'), required),
        validationRunner('gradeId', t('grade.singular'), required)
      ];
      return runValidations(nRun, validationRunners);
    };

    return (
      <Formik
        initialValues={productNRun}
        validate={validate}
        innerRef={ref}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <InputField
              className="mb-32"
              type="number"
              name="count"
              label={t('props.count')}
              value={values.count}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.count}
              displayError={touched.count || submitCount > 0}
            />
            <FormField>
              <Autocomplete
                className="mb-16"
                options={runSystems}
                label={t('runSystem.singular')}
                optionField="name"
                onChange={runSystem => {
                  setFieldValue('runSystemId', runSystem?.id);
                  setFieldValue('gradeId', null);
                  onRunSystemChange(runSystem);
                }}
                disabled={runSystems.length === 0}
                onBlur={handleBlur}
                errorMessage={errors.runSystemId}
                displayError={!!touched.runSystemId || showErrors}
              />
            </FormField>
            <FormField>
              <Autocomplete
                className="mb-16"
                options={grades}
                label={t('grade.singular')}
                optionField="name"
                onChange={grade => {
                  setFieldValue('gradeId', grade?.id);
                  setFieldValue('specificationId', null);
                  onGradeChange(grade);
                }}
                disabled={!values.runSystemId && grades.length === 0}
                onBlur={handleBlur}
                errorMessage={errors.gradeId}
                displayError={!!touched.gradeId || showErrors}
              />
            </FormField>

            <FormField>
              <Autocomplete
                className="mb-16"
                options={specifications}
                label={t('specification.singular')}
                optionField="name"
                onChange={specification => {
                  setFieldValue('specificationId', specification?.id);
                }}
                disabled={!values.gradeId && specifications.length === 0}
                onBlur={handleBlur}
              />
            </FormField>

            <ModalFooter>
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('create.verb')}
                disabled={isSubmitting}
                onClick={() => {
                  setShowErrors(true);
                }}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    );
  }
);
